// src/pages/DashboardPage.js
import React, { useState, useEffect, useCallback } from 'react';
import Nav from '../components/Nav';
import ActivityCard from '../components/ActivityCard';
import ChannelSummaryCard from '../components/ChannelSummaryCard';
import Footer from '../components/Footer';
import { useAuthContext } from '../contexts/AuthContext';
import config from '../config';
import './DashboardPage.css';

export default function DashboardPage() {
    const { API_URL } = useAuthContext();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    // Set page title
    useEffect(() => {
        document.title = `Dashboard | ${config.siteTitle}`;
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch both activities and posts
            const [activitiesRes, postsRes] = await Promise.all([
                fetch(`${API_URL}/api/activities`, { credentials: 'include' }),
                fetch(`${API_URL}/api/posts`, { credentials: 'include' })
            ]);

            let fetchedActivities = [];
            let fetchedPosts = [];

            if (activitiesRes.ok) {
                const data = await activitiesRes.json();
                fetchedActivities = data.activities || [];
            }

            if (postsRes.ok) {
                const data = await postsRes.json();
                fetchedPosts = data.posts || [];
            }

            // Tag activities
            fetchedActivities = fetchedActivities.map(a => ({
                ...a,
                type: 'activity',
                date: new Date(a.activity_date)
            }));

            // Group posts by channel
            const channelGroups = {};
            fetchedPosts.forEach(post => {
                if (post.channel_id && post.channel) {
                    const channelId = post.channel_id;
                    if (!channelGroups[channelId]) {
                        channelGroups[channelId] = {
                            channel: post.channel,
                            posts: [],
                            latestDate: new Date(post.created_at)
                        };
                    }
                    channelGroups[channelId].posts.push(post);
                    const postDate = new Date(post.created_at);
                    if (postDate > channelGroups[channelId].latestDate) {
                        channelGroups[channelId].latestDate = postDate;
                    }
                }
            });

            // Convert channel groups to feed items
            const channelItems = Object.values(channelGroups).map(group => ({
                type: 'channel-summary',
                channel: group.channel,
                latestPost: group.posts[0], // Posts are already sorted by created_at desc
                totalCount: group.posts.length,
                date: group.latestDate
            }));

            // Combine and sort
            const combined = [...fetchedActivities, ...channelItems].sort((a, b) => b.date - a.date);
            setItems(combined);

        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleUploadSuccess = () => {
        fetchData();
    };

    return (
        <div className="dashboard-page">
            <Nav onUploadSuccess={handleUploadSuccess} />

            <main className="dashboard-content">
                <section className="activity-feed">
                    <div className="page-header">
                        <h2 className="page-title">Feed</h2>
                    </div>

                    {loading ? (
                        <div className="feed-loading">Loading feed...</div>
                    ) : items.length === 0 ? (
                        <div className="feed-empty">
                            <h3>No updates yet</h3>
                            <p>Go run or post something!</p>
                        </div>
                    ) : (
                        <div className="feed-list">
                            {items.map((item) => (
                                item.type === 'activity' ? (
                                    <ActivityCard key={`activity-${item.id}`} activity={item} />
                                ) : item.type === 'channel-summary' ? (
                                    <ChannelSummaryCard
                                        key={`channel-${item.channel.id}`}
                                        channel={item.channel}
                                        latestPost={item.latestPost}
                                        totalCount={item.totalCount}
                                    />
                                ) : null
                            ))}
                        </div>
                    )}

                </section>
            </main>
            <Footer />
        </div>
    );
}

